import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { RootStore } from '~/stores/root.store';
import { IFlowTemplate, IFolder } from '~/types/model.types';
import {
    CreateTemplateIds,
    FOLDERS_UI_PERSISTENCE_KEY,
    IFoldersUiPersistence,
    IS_VISIT_IN_MY_TEMPLATES,
    MY_TEMPLATES_LAYOUT_VIEW_MODE,
    TCreateTemplateType,
    TemplatePanel,
    TFileTemplatePanel,
    TSortBy,
    TTemplatePanel
} from '../types/templates.types';
import {
    TFetchCompanyFlows,
    TFlowActionType
} from '../../CreateFlow/types/create-flow.types';
import { TRequestStatus, TemplateDeleteError } from '~/types/server.types';
import i18next from 'i18next';
import { trackError } from '~/services/logger/error.utils';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import { IFlowGalleryTemplate } from '~/components/FlowGallery/types/flow-gallery.types';
import { action } from 'mobx';
import { goToFlow } from '~/components/Builder/Common/useFlowNavigation';
import { getClickShareTemplateSource } from '~/components/Templates/templates.utils';
import type { IFlowsBetaUserType } from '~/models/user.model';
import {
    IPushToGalleryTemplatesData,
    TEMPLATE_IDS_FOR_MSPS
} from '~/services/flow-gallery.service';

export class TemplatesController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    /* STORE Utils */
    setRequestStatus(status: TRequestStatus) {
        this.rootStore.templatesUiStore.setRequestStatus(status);
    }

    setActiveFlowPanel({
        panel,
        shouldFetch = true
    }: {
        panel: TTemplatePanel;
        shouldFetch?: boolean;
    }) {
        this.rootStore.templatesUiStore.setActivePanel(panel);
        if (shouldFetch) {
            if (panel === 'Lead_forms') {
                this.fetchLeadForms({ isLeadFormEnabled: true });
                return;
            }
            this.fetchCompanyFlows();
        }
    }

    setActiveFolderPanel({
        folderId,
        shouldFetch = true
    }: {
        folderId: string;
        shouldFetch?: boolean;
    }) {
        this.rootStore.templatesUiStore.setActiveFolderPanel(folderId);
        if (shouldFetch) {
            this.fetchCompanyFlows();
        }
    }

    setActiveFileTemplatePanel(panel: TFileTemplatePanel) {
        this.rootStore.templatesUiStore.setActiveFileTemplatePanel(panel);
        this.loadFileTemplates(panel);
    }

    setFolderModalOpen(val: boolean) {
        this.rootStore.templatesUiStore.setFolderModalOpen(val);
    }

    setDeleteFlowWarningOpen(val: boolean) {
        this.rootStore.templatesUiStore.setDeleteFlowWarningOpen(val);
    }

    setLayoutViewMode(val: boolean) {
        this.rootStore.templatesUiStore.setLayoutViewMode(val);
    }

    loadFileTemplates(panel: TFileTemplatePanel) {
        const { templatesUiStore } = this.rootStore;
        const templates = templatesUiStore.getLegacyTemplatesPerPanel(panel);
        templatesUiStore.setActiveFileTemplates(templates || []);
    }

    setSelectedTemplate(template: IFlowTemplate) {
        this.rootStore.templatesUiStore.updateSelectedTemplate(template);
    }

    loadFlowTemplates(flows: IFlowTemplate[]) {
        this.rootStore.templatesUiStore.loadFlowTemplates(flows);
    }

    loadFolders(folders: IFolder[]) {
        this.rootStore.templatesUiStore.loadFolders(folders);
    }

    setSortTemplates(val: TSortBy) {
        this.rootStore.templatesUiStore.setSortTemplates(val);
    }

    findFolderById(folderId: string) {
        return this.rootStore.templatesUiStore.folders.find(
            i => i._id === folderId
        );
    }

    verifyIsVisibleActiveFolder(folderId: string) {
        const { sortedCompanyFolders: folders, maxVisibleFolders } =
            this.rootStore.templatesUiStore;
        const folder = folders
            .slice(0, maxVisibleFolders)
            .find(i => i._id === folderId);
        if (!folder) {
            this.rootStore.templatesUiStore.setShowMoreFolders(true);
        }
    }

    async fetchLeadFormTemplates() {
        const {
            templatesService,
            templatesUiStore: { loadLeadFormTemplates }
        } = this.rootStore;

        let leadFormTemplates: IFlowGalleryTemplate[] = [];
        try {
            leadFormTemplates = await templatesService.fetchLeadFormTemplates();
        } catch (error) {
            trackError({
                error,
                message:
                    '@fetchLeadFormTemplates: error occured while fetching public flow templates'
            });
        } finally {
            loadLeadFormTemplates(leadFormTemplates);
        }
    }

    async fetchLeadForms({
        isLeadFormEnabled
    }: {
        isLeadFormEnabled: boolean;
    }) {
        // TODO: merge this with fetchCompanyFlows so it'll fetch also public flows and categorize it there.
        const {
            templatesService,
            templatesUiStore: {
                leadFormTemplates,
                loadPublicFlows,
                flowFilterTypes: { activeFilters }
            },
            companyStore: { currCompany }
        } = this.rootStore;

        if (!isLeadFormEnabled) {
            return;
        }

        const params: TFetchCompanyFlows = {
            companyId: currCompany._id,
            filters: activeFilters,
            flow_type: 'public_flow'
        };

        this.setRequestStatus('fetching');

        try {
            if (!leadFormTemplates.length) {
                this.fetchLeadFormTemplates();
            }
            const leadForms = await templatesService.fetchCompanyFlows(params);
            loadPublicFlows(leadForms);
            this.setRequestStatus('succeeded');
        } catch {
            loadPublicFlows([]);
            this.setRequestStatus('failed');
        }
    }

    async fetchCompanyFlows() {
        const {
            templatesService,
            templatesUiStore,
            companyStore: { currCompany }
        } = this.rootStore;

        const {
            activePanel: { isStarred, isFolder },
            flowFilterTypes: { activeFilters },
            activeFlowPanel
        } = templatesUiStore;

        const params: TFetchCompanyFlows = {
            companyId: currCompany._id,
            filters: activeFilters,
            flow_type: 'template',
            folder_id: null
        };

        if (isStarred) {
            params.filters = [...activeFilters, 'starred'];
        }

        if (isFolder) {
            params.folder_id = activeFlowPanel;
        }

        this.setRequestStatus('fetching');

        try {
            const res = await templatesService.fetchCompanyFlows(params);
            templatesUiStore.loadFlowTemplates(res);
            this.setRequestStatus('succeeded');
        } catch {
            templatesUiStore.loadFlowTemplates([]);
            this.setRequestStatus('failed');
        }
    }

    setFlowFilterType(val: TFlowActionType) {
        const {
            templatesUiStore: {
                activePanel: { isLeadForms }
            }
        } = this.rootStore;
        this.rootStore.templatesUiStore.setFlowFilterType(val);
        if (isLeadForms) {
            this.fetchLeadForms({ isLeadFormEnabled: true });
            return;
        }
        this.fetchCompanyFlows();
    }

    /* FLOW TEMPLATES APIs */
    async createFlowTemplate(flowId: string = null, title: string = null) {
        const {
            flowService,
            templatesUiStore: { addFlowTemplate, activeFlowPanel: folderId }
        } = this.rootStore;
        const flow = await flowService.createFlowTemplate(flowId, title);
        /* saves newly added template to the activeFlowPanel */
        this.moveTemplateToFolder(folderId, flow._id);
        addFlowTemplate(flow);
        return flow;
    }

    async renameFlowTemplate(flowId: string, newName: string) {
        const {
            flowService,
            fullstoryService,
            templatesUiStore: { renameFlowTemplate }
        } = this.rootStore;
        try {
            await flowService.updateFlow(flowId, { title: newName });
            renameFlowTemplate(flowId, newName);
        } catch (error) {
            fullstoryService.error('Failed to rename this template');
            this.rootStore.toastStore.add({
                text: 'Failed to rename this template',
                type: ToastType.Error
            });

            throw error;
        }
    }

    async deleteFlowTemplate({ flowId }: { flowId: string }) {
        const {
            flowService,
            fullstoryService,
            templatesUiStore: { deleteFlowTemplate }
        } = this.rootStore;
        try {
            await flowService.deleteFlow(flowId);
            deleteFlowTemplate(flowId);
        } catch (error) {
            let err_msg;
            switch (error.data.error_type) {
                case TemplateDeleteError.CannotDeleteWorkflowRelatedTemplate: {
                    const defaultMessage =
                        'Cannot delete this template because it is attached to an automation template';
                    err_msg = error.data.error_message || defaultMessage;
                    break;
                }
                case TemplateDeleteError.CannotDeleteDraftRelatedTemplate: {
                    const defaultMessage =
                        'Cannot delete this template because it is attached to another smart file(s) as draft';
                    err_msg = error.data.error_message || defaultMessage;
                    break;
                }
                case TemplateDeleteError.HBEntityLinkedToWorkflowsError: {
                    // we do not want a toast for this error, it will be handled separately by the calling component
                    break;
                }
                default: {
                    err_msg = 'Cannot delete this template';
                    break;
                }
            }
            if (err_msg) {
                fullstoryService.error(err_msg);
                this.rootStore.toastStore.add({
                    text: err_msg,
                    type: ToastType.Error
                });
            }

            throw error;
        }
    }

    async starFlowTemplate(flowId: string, starred: boolean) {
        const {
            flowService,
            templatesUiStore: { starFlowTemplate }
        } = this.rootStore;
        await flowService.updateFlow(flowId, {
            starred
        });
        starFlowTemplate(flowId, starred);
    }

    async moveTemplateToFolder(folderId: string, flowId: string) {
        const {
            flowService,
            templatesUiStore: { flowTemplates }
        } = this.rootStore;
        await flowService.updateFlow(flowId, {
            folder_id: folderId
        });
        const folder = this.findFolderById(folderId);
        const flows = flowTemplates.map(item =>
            item._id === flowId
                ? {
                      ...item,
                      ...{
                          folder_id: folderId,
                          path: folder ? folder.path : null
                      }
                  }
                : item
        );
        this.loadFlowTemplates(flows);
    }

    /* FOLDERS APIs */
    async createFolder(folderName: string) {
        const {
            templatesUiStore: { addNewFolder },
            companyStore: { currCompany },
            companyService
        } = this.rootStore;
        try {
            const newFolder = await companyService.createFolder(
                currCompany._id,
                folderName
            );
            addNewFolder(newFolder);
            return newFolder;
        } catch (err) {
            // This is a workaround: because somehow the calling function cannot catch this exception so we return undefined in case of an eror and test for that
            return undefined;
        }
    }

    async updateFolder(folder: IFolder) {
        const {
            companyService,
            templatesUiStore: { updateFolder }
        } = this.rootStore;
        const updatedFolder = await companyService.updateFolder(folder);
        updateFolder(updatedFolder);
    }

    async updateFolderName(folder: IFolder) {
        const {
            companyService,
            templatesUiStore: { updateFolderName }
        } = this.rootStore;
        const updatedFolder = await companyService.updateFolder(folder);
        updateFolderName(updatedFolder);
    }

    async deleteFolder(folder: IFolder) {
        const {
            companyService,
            templatesUiStore: { deleteFolder }
        } = this.rootStore;
        await companyService.deleteFolder(folder);
        this.setActiveFlowPanel({ panel: TemplatePanel.All_flows });
        deleteFolder(folder);
    }

    /* Import template */
    async importFlowTemplate(templateId: string) {
        const {
            templatesUiStore: { activeFlowPanel: folderId },
            templatesService
        } = this.rootStore;
        const res = await templatesService.saveTemplate(templateId);
        const importedFlow = res.imported_templates[0];
        this.moveTemplateToFolder(folderId, importedFlow._id);
        return importedFlow;
    }

    async createCustomTemplate(blocksArray: TCreateTemplateType[]) {
        const { templatesService } = this.rootStore;
        if (!blocksArray || blocksArray.length == 0) {
            return this.createFlowTemplate();
        } else if (blocksArray.length == 1) {
            return this.importFlowTemplate(CreateTemplateIds[blocksArray[0]]);
        } else {
            const customTemplate = await this.importFlowTemplate(
                CreateTemplateIds.Custom
            );
            await templatesService.modifyCustomTemplate(
                customTemplate._id,
                blocksArray
            );
            return customTemplate;
        }
    }

    async previewSharedTemplate(metadataId, track) {
        const {
            templatesService,
            templatesUiStore: {
                setSharedTemplate,
                setSharedTemplatePermissionsError
            },
            userStore
        } = this.rootStore;
        try {
            const template = await templatesService.previewSharedTemplate(
                metadataId
            );
            setSharedTemplate(template);
            setSharedTemplatePermissionsError(null);
            track(AnalyticsEvents.OpenSharedTemplatePreviewModal, {
                template_title: template.title,
                receiver_email: userStore.currUser.email
            });
        } catch (error) {
            trackError({
                error,
                message: 'An error on preview shared template'
            });
            setSharedTemplatePermissionsError(error.data?.error_type || '');
            track(AnalyticsEvents.OpenSharedTemplatePreviewModalError, {
                shared_template_metadata_id: metadataId,
                receiver_email: userStore.currUser.email
            });
        }
    }

    async saveSharedTemplate(
        metadataId,
        folderId,
        folderName,
        history,
        track,
        isMweb
    ) {
        const {
            templatesService,
            templatesUiStore: {
                sharedTemplate,
                setIsLoadingStatus,
                setActiveFolderPanel,
                addFlowTemplate
            },
            userStore
        } = this.rootStore;
        setIsLoadingStatus(true);
        const t = i18next.getFixedT(
            null,
            'templates',
            'share_template.preview_template_modal'
        );
        try {
            const savedTemplate = await templatesService.saveSharedTemplate(
                metadataId,
                folderId
            );
            addFlowTemplate(savedTemplate);
            track(AnalyticsEvents.SaveSharedFlowTemplateSuccess, {
                saved_template_id: savedTemplate._id,
                folder_name: folderName,
                receiver_email: userStore.currUser.email
            });
            this.rootStore.toastStore.add({
                type: ToastType.Success,
                title: `Copy of ${sharedTemplate.title} was successfully saved!`,
                text: `You can find the template in “My Templates” under “${folderName}”`,
                actions: [
                    {
                        size: 'sm',
                        theme: 'secondary-invisible-alternative',
                        text: isMweb ? t('success_cta_mweb') : t('success_cta'),
                        onClick: () => {
                            track(AnalyticsEvents.GoToFlowTemplate, {
                                type: 'flow',
                                template_name: savedTemplate.title
                            });
                            goToFlow({
                                flowId: savedTemplate._id,
                                isReplace: true,
                                history
                            });
                        }
                    }
                ]
            });
        } catch (error) {
            trackError({
                error,
                message: 'An error on saving shared template'
            });
            track(AnalyticsEvents.SaveSharedFlowTemplateError, {
                metadata_id: metadataId,
                template_id: sharedTemplate._id,
                folder_name: folderName,
                receiver_email: userStore.currUser.email
            });
        } finally {
            setActiveFolderPanel(folderId);
            setIsLoadingStatus(false);
        }
    }

    async loadCompany() {
        const { sessionStore, companyCtrl } = this.rootStore;
        await companyCtrl.fetchCompany(sessionStore.currCompanyId);
    }

    async shareTemplate(track) {
        const {
            templatesService,
            templatesUiStore,
            userStore,
            actionsManager
        } = this.rootStore;
        const t = i18next.getFixedT(
            null,
            'templates',
            'share_template.sender_modal'
        );
        templatesUiStore.setIsLoadingStatus(true);
        try {
            await actionsManager.requestsQueue.onPostRequestsExecution();

            await templatesService.shareTemplate(
                templatesUiStore.selectedTemplate?._id,
                templatesUiStore.sharedWithEmail
            );
            track(AnalyticsEvents.ShareFlowTemplateSuccess, {
                sender_email: userStore.currUser.email,
                receiver_email: templatesUiStore.sharedWithEmail,
                share_template_title: templatesUiStore.selectedTemplate?.title,
                template_id: templatesUiStore.selectedTemplate?._id,
                source: getClickShareTemplateSource()
            });
            templatesUiStore.closeShareModal();
            this.rootStore.toastStore.add({
                type: ToastType.Success,
                text: t('success_message')
            });
        } catch (error) {
            trackError({ error, message: 'An error on sharing template' });
            track(AnalyticsEvents.ShareFlowTemplateError, {
                sender_email: userStore.currUser.email,
                receiver_email: templatesUiStore.sharedWithEmail,
                share_template_title: templatesUiStore.selectedTemplate?.title
            });
            this.rootStore.toastStore.add({
                type: ToastType.Error,
                text: t('share_error')
            });
        } finally {
            templatesUiStore.setIsLoadingStatus(false);
        }
    }

    publishTemplate(templateId: string) {
        const { templatesService } = this.rootStore;
        return templatesService.publishTemplate(templateId, true);
    }

    deactivateTemplate(templateId: string) {
        const { templatesService } = this.rootStore;
        return templatesService.publishTemplate(templateId, false);
    }

    changePublicTemplateConfiguration(
        templateId: string,
        configType: string,
        state: boolean
    ) {
        const { templatesService } = this.rootStore;
        return templatesService.changePublicTemplateConfiguration({
            templateId,
            configType,
            state
        });
    }

    updatePublicConfigurationSelledByTemplate(
        templateId: string,
        leadFormId: string
    ) {
        const { templatesService } = this.rootStore;
        return templatesService.updatePublicConfigurationSelledByTemplate({
            templateId,
            leadFormId
        });
    }

    setFoldersUiPersistence() {
        const {
            uiPersistenceStore,
            templatesUiStore: {
                activeFlowPanel,
                activeFileTemplatePanel,
                isFolderPanelActive
            }
        } = this.rootStore;

        const value = activeFlowPanel || activeFileTemplatePanel;

        const type = (() => {
            if (activeFileTemplatePanel) {
                return 'file';
            }
            if (isFolderPanelActive) {
                return 'folder';
            }
            return 'panel';
        })();

        const nextPanel: IFoldersUiPersistence = { value, type };

        uiPersistenceStore.set(FOLDERS_UI_PERSISTENCE_KEY, nextPanel);
    }

    getLayoutUiPersistence(userType: IFlowsBetaUserType) {
        // MY_TEMPLATES_LAYOUT_VIEW_MODE -  true = grid view , false = list view
        const {
            userStore: { currUser },
            uiPersistenceStore
        } = this.rootStore;
        if (userType.hasFlows) {
            if (currUser.isInTrial) {
                const isVisitInMyTemplates = uiPersistenceStore.get(
                    `${IS_VISIT_IN_MY_TEMPLATES}.isVisitInMyTemplates`,
                    false
                );
                if (isVisitInMyTemplates) {
                    this.setLayOutUiPersistence();
                } else {
                    uiPersistenceStore.set(
                        `${IS_VISIT_IN_MY_TEMPLATES}.isVisitInMyTemplates`,
                        true
                    );
                    uiPersistenceStore.set(
                        `${MY_TEMPLATES_LAYOUT_VIEW_MODE}.isGridView`,
                        false
                    );
                    this.setLayoutViewMode(false);
                }
            } else {
                this.setLayOutUiPersistence();
            }
        }
    }

    setLayOutUiPersistence() {
        const { uiPersistenceStore } = this.rootStore;
        const isGridView = uiPersistenceStore.get(
            `${MY_TEMPLATES_LAYOUT_VIEW_MODE}.isGridView`,
            true
        );

        if (!isGridView) {
            this.setLayoutViewMode(isGridView);
        }
    }

    isTemplatePanel(value: string): value is TTemplatePanel {
        return Object.values(TemplatePanel).includes(value as TemplatePanel);
    }

    getFoldersUiPersistence() {
        const { uiPersistenceStore } = this.rootStore;

        const prevPanel = uiPersistenceStore.get(FOLDERS_UI_PERSISTENCE_KEY, {
            type: 'panel',
            value: 'All_flows'
        });
        switch (prevPanel.type) {
            case 'panel':
                // since lead forms moved to a new page - if the prevPanel was lead forms we want to open 'All flows' on initial templates load
                const panel =
                    prevPanel.value === 'Lead_forms'
                        ? 'All_flows'
                        : prevPanel.value;

                this.setActiveFlowPanel({
                    panel: panel as TTemplatePanel,
                    shouldFetch: false
                });
                break;
            case 'folder':
                this.setActiveFolderPanel({
                    folderId: prevPanel.value,
                    shouldFetch: false
                });
                this.verifyIsVisibleActiveFolder(prevPanel.value);
                break;
            case 'file':
                this.setActiveFileTemplatePanel(
                    prevPanel.value as TFileTemplatePanel
                );
                break;
            default:
                this.setActiveFlowPanel({
                    panel: 'All_flows',
                    shouldFetch: false
                });
                break;
        }
    }

    /* Initialization */
    async load(
        redirectToFolderId: string,
        redirectToPanel: TTemplatePanel | TFileTemplatePanel,
        isMediumUp: boolean,
        isLargeUp: boolean,
        userType: IFlowsBetaUserType,
        isLeadFormEnabled: boolean,
        isLeadCaptureScreen: boolean
    ) {
        const {
            companyStore: { currCompany },
            templatesUiStore
        } = this.rootStore;
        if (isLargeUp) {
            this.getLayoutUiPersistence(userType);
        }

        const _redirectToPanel = isLeadCaptureScreen
            ? 'Lead_forms'
            : redirectToPanel;

        templatesUiStore.setRequestStatus('initial_load');
        await this.loadCompany();

        if (isLeadCaptureScreen) {
            this.fetchLeadForms({ isLeadFormEnabled });
        } else {
            this.fetchCompanyFlows();
            this.loadFolders(currCompany.folders);

            if (!isMediumUp) {
                this.setActiveFlowPanel({
                    panel: 'All_flows',
                    shouldFetch: false
                });
                return;
            }
            if (redirectToFolderId) {
                const folder = this.findFolderById(redirectToFolderId);
                if (folder) {
                    this.setActiveFolderPanel({
                        folderId: redirectToFolderId,
                        shouldFetch: false
                    });
                }
                return;
            }
        }

        if (_redirectToPanel) {
            if (this.isTemplatePanel(_redirectToPanel)) {
                this.setActiveFlowPanel({
                    panel: _redirectToPanel,
                    shouldFetch: false
                });
            } else {
                this.setActiveFileTemplatePanel(_redirectToPanel);
            }

            return;
        }

        this.getFoldersUiPersistence();
    }

    async fetchPushToGalleryTemplates(
        industry: string,
        show_templates_for_msp: boolean = false
    ) {
        const { templatesUiStore, flowGalleryService } = this.rootStore;
        let templatesData: IPushToGalleryTemplatesData;

        if (show_templates_for_msp) {
            try {
                templatesData = await flowGalleryService.listFlowsByIDs(
                    TEMPLATE_IDS_FOR_MSPS
                );
            } catch (error) {
                templatesData =
                    await flowGalleryService.listPushToGalleryTemplates(
                        industry
                    );
            }
        } else {
            templatesData = await flowGalleryService.listPushToGalleryTemplates(
                industry
            );
        }

        templatesUiStore.setPushToGalleryTemplates(templatesData);
    }

    @action.bound
    async togglePublicFlowActivation(flowId: string, isActive: boolean) {
        // this function only handles the flag and does NOT publish/ update the flow
        const { flowService, toastStore } = this.rootStore;

        let response;
        try {
            if (isActive) {
                this.rootStore.templatesUiStore.togglePublicFlowTemplateStatus(
                    flowId,
                    true
                );
                response = await flowService.activatePublicFlow(flowId);
                toastStore.add({
                    type: ToastType.Success,
                    text: 'Your lead form is now live.'
                });
            } else {
                this.rootStore.templatesUiStore.togglePublicFlowTemplateStatus(
                    flowId,
                    false
                );
                response = await flowService.deactivatePublicFlow(flowId);
                toastStore.add({
                    type: ToastType.Info,
                    text: 'This lead form is now inactive and is no longer accessible.'
                });
            }

            return response;
        } catch (error) {
            trackError({ error });
            toastStore.add({
                type: ToastType.Error,
                text: 'Something went wrong'
            });
            throw new Error(error);
        }
    }

    reset() {
        this.rootStore.templatesUiStore.reset();
    }

    goToJohnBranchFolder() {
        const {
            companyStore: { currCompany },
            templatesUiStore: { activeFlowPanel }
        } = this.rootStore;

        const johnBranchFolderId = currCompany?.johnBranchFolderId;
        if (johnBranchFolderId && activeFlowPanel !== johnBranchFolderId) {
            this.setActiveFolderPanel({
                folderId: johnBranchFolderId
            });
            this.setFoldersUiPersistence();
        }
    }

    @action.bound
    async updateTemplates() {
        await this.loadCompany();
        this.loadFileTemplates(
            this.rootStore.templatesUiStore.activeFileTemplatePanel
        );
    }
}
